import { z } from 'zod';
import { type Pagination, paginationToQuery } from '@/generic/pagination';
import type { RulesFilter } from '@/rule/domain/RulesFilter.ts';
import { severityToJson, statusToJson } from '@/rule/secondary/CleanApiRulesJson.ts';

const DEFAULT_ORGANIZATION_PARAM = 'default';

const organizationQueryOf = (filter: RulesFilter): string | undefined => {
  switch (filter.scope.kind) {
    case 'ALL':
      return undefined;
    case 'DEFAULT':
      return DEFAULT_ORGANIZATION_PARAM;
    case 'ORGANIZATION':
      return filter.scope.organizationId.value;
  }
};

const statusQueryOf = (filter: RulesFilter): string | undefined =>
  filter.status.map(statusToJson).orUndefined();

const severityQueryOf = (filter: RulesFilter): string | undefined =>
  filter.severity.map(severityToJson).orUndefined();

export const rulesListQuerySchema = z.object({
  page: z.coerce.string(),
  'page-size': z.coerce.string(),
  filter: z.string().optional(),
  organization: z.string().optional(),
  'active-only': z.string().optional(),
  status: z.string().optional(),
  severity: z.string().optional(),
});

export type RulesListQuery = z.infer<typeof rulesListQuerySchema>;

export const rulesListQueryOf = (
  pagination: Pagination,
  filter: RulesFilter,
  activeOnly = false,
): RulesListQuery =>
  rulesListQuerySchema.parse({
    ...paginationToQuery(pagination),
    filter: filter.text.orUndefined(),
    organization: organizationQueryOf(filter),
    'active-only': activeOnly ? 'true' : undefined,
    status: statusQueryOf(filter),
    severity: severityQueryOf(filter),
  });
